import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../config';
import { useOrganization } from '../../context/OrganizationContext';
import { useStripe } from '../../context/StripeContext';
import { fetchWithOrg } from '../../utils/api';

import {
  ArrowCircleUp,
  ArrowCounterClockwise,
  ArrowCircleDown,
  Buildings,
  Calendar,
  CaretRight,
  ChatText,
  Check,
  CircleNotch,
  Cpu,
  CreditCard,
  Crown,
  Info,
  Lightning,
  Microphone,
  Robot,
  Sparkle,
  Star,
  Trash,
  UsersThree,
  Warning,
  WarningCircle,
  X
} from '@phosphor-icons/react';
const BillingManagementModal = ({ isOpen, onClose, currentPlan, onPlanChange }) => {
  const { activeOrganization, organizations } = useOrganization();
  const { createSubscriptionCheckout, loading: stripeLoading } = useStripe();
  const [activeTab, setActiveTab] = useState('subscription');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [upgradingPlan, setUpgradingPlan] = useState(null);
  
  // Billing data
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [seats, setSeats] = useState([]);
  const [agents, setAgents] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  
  // Confirmation states
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
  const [showCancelSeatConfirm, setShowCancelSeatConfirm] = useState(null);
  const [showCancelAgentConfirm, setShowCancelAgentConfirm] = useState(null);
  const [selectedSeatToCancel, setSelectedSeatToCancel] = useState(null);
  const [selectedAgentToCancel, setSelectedAgentToCancel] = useState(null);
  const [downgradeImpact, setDowngradeImpact] = useState(null);
  const [loadingImpact, setLoadingImpact] = useState(false);

  // Fetch downgrade impact when showing confirmation
  const fetchDowngradeImpact = useCallback(async () => {
    if (!activeOrganization?.organization_id) return;
    
    setLoadingImpact(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/billing/downgrade-impact`,
        { method: 'GET' },
        activeOrganization.organization_id
      );
      if (response.ok) {
        const data = await response.json();
        setDowngradeImpact(data);
      }
    } catch (error) {
      console.error('Error fetching downgrade impact:', error);
    } finally {
      setLoadingImpact(false);
    }
  }, [activeOrganization?.organization_id]);

  // Fetch billing data
  const fetchBillingData = useCallback(async () => {
    if (!activeOrganization?.organization_id) return;
    
    setLoading(true);
    try {
      // Fetch subscription details
      const subResponse = await fetchWithOrg(
        `${BACKEND_URL}/api/billing/subscription`,
        { method: 'GET' },
        activeOrganization.organization_id
      );
      if (subResponse.ok) {
        const data = await subResponse.json();
        setSubscriptionData(data);
      }

      // Fetch seats
      const seatsResponse = await fetchWithOrg(
        `${BACKEND_URL}/api/billing/seats`,
        { method: 'GET' },
        activeOrganization.organization_id
      );
      if (seatsResponse.ok) {
        const data = await seatsResponse.json();
        setSeats(data.seats || []);
      }

      // Fetch agents with subscriptions from Stripe routes
      const agentsResponse = await fetch(
        `${BACKEND_URL}/api/stripe/agent-subscriptions`,
        { 
          method: 'GET',
          credentials: 'include',
          headers: {
            'X-Organization-Id': activeOrganization.organization_id
          }
        }
      );
      if (agentsResponse.ok) {
        const data = await agentsResponse.json();
        setAgents(data.agents || []);
      }

    } catch (error) {
      console.error('Error fetching billing data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeOrganization?.organization_id]);

  useEffect(() => {
    if (isOpen) {
      fetchBillingData();
    }
  }, [isOpen, fetchBillingData]);

  // Handle plan change (upgrade/downgrade)
  const handlePlanChange = async (newPlan) => {
    setProcessing(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/billing/change-plan`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ new_plan: newPlan })
        },
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Plan updated successfully');
        setShowDowngradeConfirm(false);
        fetchBillingData();
        if (onPlanChange) onPlanChange(newPlan);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to change plan');
      }
    } catch (error) {
      toast.error('Failed to change plan');
    } finally {
      setProcessing(false);
    }
  };

  // Handle Stripe checkout for upgrades
  const handleStripeUpgrade = async (planType) => {
    const planId = `${planType}_monthly`; // Default to monthly
    setUpgradingPlan(planType);
    try {
      await createSubscriptionCheckout(planId);
      // User will be redirected to Stripe checkout
    } catch (error) {
      console.error('Stripe checkout error:', error);
      toast.error(error.message || 'Failed to start checkout. Please try again.');
      setUpgradingPlan(null);
    }
  };

  // Cancel seat subscription
  const handleCancelSeat = async (seatId) => {
    setProcessing(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/billing/seats/${seatId}/cancel`,
        { method: 'POST' },
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Seat subscription cancelled. Access continues until end of billing period.');
        setShowCancelSeatConfirm(null);
        setSelectedSeatToCancel(null);
        fetchBillingData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel seat');
      }
    } catch (error) {
      toast.error('Failed to cancel seat subscription');
    } finally {
      setProcessing(false);
    }
  };

  // Cancel agent subscription
  const handleCancelAgent = async (agentId, organizationId) => {
    setProcessing(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/stripe/cancel-agent-subscription/${agentId}`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Organization-Id': organizationId || activeOrganization.organization_id
          },
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Agent subscription cancelled. Access continues until end of billing period.');
        setShowCancelAgentConfirm(null);
        setSelectedAgentToCancel(null);
        fetchBillingData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel agent');
      }
    } catch (error) {
      toast.error('Failed to cancel agent subscription');
    } finally {
      setProcessing(false);
    }
  };

  // Reinstate subscription
  const handleReinstateSubscription = async () => {
    setProcessing(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/billing/reinstate-subscription`,
        { method: 'POST' },
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Subscription reinstated successfully');
        fetchBillingData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reinstate subscription');
      }
    } catch (error) {
      toast.error('Failed to reinstate subscription');
    } finally {
      setProcessing(false);
    }
  };

  // Reinstate seat subscription
  const handleReinstateSeat = async (seatId) => {
    setProcessing(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/billing/seats/${seatId}/reinstate`,
        { method: 'POST' },
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Seat subscription reinstated');
        fetchBillingData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reinstate seat');
      }
    } catch (error) {
      toast.error('Failed to reinstate seat subscription');
    } finally {
      setProcessing(false);
    }
  };

  // Reinstate agent subscription
  const handleReinstateAgent = async (agentId, organizationId) => {
    setProcessing(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/stripe/reinstate-agent-subscription/${agentId}`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Organization-Id': organizationId || activeOrganization.organization_id
          },
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Agent subscription reinstated');
        fetchBillingData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reinstate agent');
      }
    } catch (error) {
      toast.error('Failed to reinstate agent subscription');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPrice = (amount) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  const getPlanInfo = (planId) => {
    const plans = {
      starter: { name: 'Basic', price: 'Free', icon: Lightning, color: 'text-gray-400', gradient: 'from-gray-500 to-gray-600' },
      basic: { name: 'Basic', price: 'Free', icon: Lightning, color: 'text-gray-400', gradient: 'from-gray-500 to-gray-600' },
      free: { name: 'Basic', price: 'Free', icon: Lightning, color: 'text-gray-400', gradient: 'from-gray-500 to-gray-600' },
      professional: { name: 'Professional', price: '$39/mo', icon: Sparkle, color: 'text-emerald-400', gradient: 'from-[#10B981] to-[#6366F1]' },
      builder: { name: 'Professional', price: '$39/mo', icon: Sparkle, color: 'text-emerald-400', gradient: 'from-[#10B981] to-[#6366F1]' },
      teams: { name: 'Teams', price: '$99/mo', icon: Star, color: 'text-purple-400', gradient: 'from-[#6366F1] to-[#8B5CF6]' },
      pro: { name: 'Teams', price: '$99/mo', icon: Star, color: 'text-purple-400', gradient: 'from-[#6366F1] to-[#8B5CF6]' },
      partner_pro: { name: 'Partner Pro', price: '$299/mo', icon: Crown, color: 'text-pink-400', gradient: 'from-[#EC4899] to-[#8B5CF6]' }
    };
    return plans[planId] || plans.basic;
  };

  if (!isOpen) return null;

  const planInfo = getPlanInfo(currentPlan);
  const PlanIcon = planInfo.icon;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden bg-[#0F0F0F] rounded-2xl border border-[rgba(255,255,255,0.1)] shadow-2xl"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#0F0F0F] border-b border-[rgba(255,255,255,0.1)] px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${planInfo.gradient} flex items-center justify-center`}>
                  <CreditCard size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Manage Billing</h2>
                  <p className="text-sm text-gray-400">Manage your subscription and seats</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-[rgba(255,255,255,0.1)]"
              >
                <X size={20} />
              </button>
            </div>

            {/* Tabs - Seats only visible for Teams and Partner Pro */}
            <div className="flex gap-1 mt-4 bg-[rgba(255,255,255,0.05)] p-1 rounded-xl">
              {[
                { id: 'subscription', label: 'Subscription', icon: Crown },
                ...((currentPlan === 'teams' || currentPlan === 'pro' || currentPlan === 'partner_pro') 
                  ? [{ id: 'seats', label: 'Seats', icon: UsersThree }] 
                  : [])
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-[rgba(255,255,255,0.1)] text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <tab.icon size={16} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <CircleNotch size={32} className="text-purple-500 animate-spin" />
              </div>
            ) : (
              <>
                {/* Subscription Tab */}
                {activeTab === 'subscription' && (
                  <div className="space-y-6">
                    {/* Current Plan */}
                    <div className="p-6 bg-[rgba(255,255,255,0.03)] rounded-xl border border-[rgba(255,255,255,0.1)]">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${planInfo.gradient} flex items-center justify-center`}>
                            <PlanIcon size={28} className="text-white" />
                          </div>
                          <div>
                            <div className="flex items-center gap-3">
                              <h3 className="text-xl font-bold text-white">{planInfo.name} Plan</h3>
                              {subscriptionData?.cancel_at_period_end && (
                                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-medium rounded-full">
                                  Cancellation Pending
                                </span>
                              )}
                            </div>
                            <p className="text-2xl font-bold text-white mt-1">{planInfo.price}</p>
                            {subscriptionData?.current_period_end && (
                              <p className={`text-sm mt-2 flex items-center gap-2 ${subscriptionData.cancel_at_period_end ? 'text-red-400' : 'text-gray-400'}`}>
                                <Calendar size={14} />
                                {subscriptionData.cancel_at_period_end 
                                  ? `Cancels on ${formatDate(subscriptionData.current_period_end)}`
                                  : `Renews on ${formatDate(subscriptionData.current_period_end)}`
                                }
                              </p>
                            )}
                          </div>
                        </div>
                        {/* Reinstate button */}
                        {subscriptionData?.cancel_at_period_end && (
                          <button
                            onClick={handleReinstateSubscription}
                            disabled={processing}
                            className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors text-sm font-medium disabled:opacity-50"
                          >
                            {processing ? (
                              <CircleNotch size={16} className="animate-spin" />
                            ) : (
                              <ArrowCounterClockwise size={16} />
                            )}
                            Reinstate
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Plan Options */}
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-4">Change Plan</h4>
                      <div className="grid gap-4">
                        {/* Upgrade to Professional */}
                        {(currentPlan === 'starter' || currentPlan === 'basic' || currentPlan === 'free' || !currentPlan) && (
                          <button
                            onClick={() => handleStripeUpgrade('professional')}
                            disabled={processing || stripeLoading}
                            className="flex items-center justify-between p-4 bg-[rgba(16,185,129,0.1)] border border-[rgba(16,185,129,0.3)] rounded-xl hover:border-[rgba(16,185,129,0.5)] transition-colors disabled:opacity-50"
                          >
                            <div className="flex items-center gap-3">
                              {upgradingPlan === 'professional' ? (
                                <CircleNotch size={20} className="text-emerald-400 animate-spin" />
                              ) : (
                                <ArrowCircleUp size={20} className="text-emerald-400" />
                              )}
                              <div className="text-left">
                                <p className="font-semibold text-white">Upgrade to Professional</p>
                                <p className="text-sm text-gray-400">$39/mo — 2,000 credits, 30 voice mins</p>
                              </div>
                            </div>
                            <CaretRight size={20} className="text-gray-400" />
                          </button>
                        )}

                        {/* Upgrade to Teams */}
                        {(currentPlan === 'starter' || currentPlan === 'basic' || currentPlan === 'free' || currentPlan === 'professional' || currentPlan === 'builder' || !currentPlan) && (
                          <button
                            onClick={() => handleStripeUpgrade('teams')}
                            disabled={processing || stripeLoading}
                            className="flex items-center justify-between p-4 bg-[rgba(99,102,241,0.1)] border border-[rgba(99,102,241,0.3)] rounded-xl hover:border-[rgba(99,102,241,0.5)] transition-colors disabled:opacity-50"
                          >
                            <div className="flex items-center gap-3">
                              {upgradingPlan === 'teams' ? (
                                <CircleNotch size={20} className="text-purple-400 animate-spin" />
                              ) : (
                                <ArrowCircleUp size={20} className="text-purple-400" />
                              )}
                              <div className="text-left">
                                <p className="font-semibold text-white">Upgrade to Teams</p>
                                <p className="text-sm text-gray-400">$99/mo — 6,000 credits, 100 voice mins, 10 users</p>
                              </div>
                            </div>
                            <CaretRight size={20} className="text-gray-400" />
                          </button>
                        )}

                        {/* Upgrade to Partner Pro */}
                        {(currentPlan !== 'partner_pro') && (
                          <button
                            onClick={() => handleStripeUpgrade('partner_pro')}
                            disabled={processing || stripeLoading}
                            className="flex items-center justify-between p-4 bg-[rgba(236,72,153,0.1)] border border-[rgba(236,72,153,0.3)] rounded-xl hover:border-[rgba(236,72,153,0.5)] transition-colors disabled:opacity-50"
                          >
                            <div className="flex items-center gap-3">
                              {upgradingPlan === 'partner_pro' ? (
                                <CircleNotch size={20} className="text-pink-400 animate-spin" />
                              ) : (
                                <ArrowCircleUp size={20} className="text-pink-400" />
                              )}
                              <div className="text-left">
                                <p className="font-semibold text-white">Upgrade to Partner Pro</p>
                                <p className="text-sm text-gray-400">$299/mo — 20,000 credits, 300 voice mins, multi-tenant</p>
                              </div>
                            </div>
                            <CaretRight size={20} className="text-gray-400" />
                          </button>
                        )}

                        {/* Downgrade options - show ALL lower tiers */}
                        {(currentPlan === 'partner_pro') && (
                          <>
                            <button
                              onClick={() => setShowDowngradeConfirm('teams')}
                              className="flex items-center justify-between p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-xl hover:border-[rgba(255,255,255,0.2)] transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <ArrowCircleDown size={20} className="text-gray-400" />
                                <div className="text-left">
                                  <p className="font-medium text-white">Downgrade to Teams</p>
                                  <p className="text-sm text-gray-400">$99/mo — 6,000 credits, 10 users</p>
                                </div>
                              </div>
                              <CaretRight size={20} className="text-gray-400" />
                            </button>
                            <button
                              onClick={() => setShowDowngradeConfirm('professional')}
                              className="flex items-center justify-between p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-xl hover:border-[rgba(255,255,255,0.2)] transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <ArrowCircleDown size={20} className="text-gray-400" />
                                <div className="text-left">
                                  <p className="font-medium text-white">Downgrade to Professional</p>
                                  <p className="text-sm text-gray-400">$39/mo — 2,000 credits, single user</p>
                                </div>
                              </div>
                              <CaretRight size={20} className="text-gray-400" />
                            </button>
                          </>
                        )}

                        {(currentPlan === 'teams' || currentPlan === 'pro') && (
                          <button
                            onClick={() => setShowDowngradeConfirm('professional')}
                            className="flex items-center justify-between p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-xl hover:border-[rgba(255,255,255,0.2)] transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <ArrowCircleDown size={20} className="text-gray-400" />
                              <div className="text-left">
                                <p className="font-medium text-white">Downgrade to Professional</p>
                                <p className="text-sm text-gray-400">$39/mo — 2,000 credits</p>
                              </div>
                            </div>
                            <CaretRight size={20} className="text-gray-400" />
                          </button>
                        )}

                        {/* Cancel subscription (downgrade to Basic) */}
                        {currentPlan && currentPlan !== 'starter' && currentPlan !== 'basic' && currentPlan !== 'free' && (
                          <button
                            onClick={() => {
                              setShowDowngradeConfirm('starter');
                              fetchDowngradeImpact();
                            }}
                            className="flex items-center justify-between p-4 bg-[rgba(239,68,68,0.05)] border border-[rgba(239,68,68,0.2)] rounded-xl hover:border-[rgba(239,68,68,0.4)] transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <Trash size={20} className="text-red-400" />
                              <div className="text-left">
                                <p className="font-medium text-white">Cancel Subscription</p>
                                <p className="text-sm text-gray-400">Revert to Basic (Free) at end of billing period</p>
                              </div>
                            </div>
                            <CaretRight size={20} className="text-gray-400" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Info Note */}
                    <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                      <div className="flex items-start gap-3">
                        <Info size={18} className="text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-400">
                          <p className="font-medium">Monthly Recurring Subscriptions</p>
                          <p className="text-blue-400/80 mt-1">
                            All subscriptions are billed monthly. Changes take effect at the start of your next billing cycle.
                            You&apos;ll continue to have access to your current plan until then.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Seats Tab */}
                {activeTab === 'seats' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-lg font-semibold text-white">Team Seats</h4>
                        <p className="text-sm text-gray-400">
                          {currentPlan === 'partner_pro' 
                            ? 'Up to 10 users included. Purchase additional seats as needed.'
                            : 'Up to 10 users included with your Teams plan.'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-white">{seats.length}</p>
                        <p className="text-sm text-gray-400">Additional seats</p>
                      </div>
                    </div>

                    {/* Plan-specific seat info */}
                    {(currentPlan === 'teams' || currentPlan === 'pro') && (
                      <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
                        <div className="flex items-start gap-3">
                          <Info size={18} className="text-purple-400 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-purple-400">
                            <p className="font-medium">Teams Plan — 10 Users Included</p>
                            <p className="text-purple-400/80 mt-1">
                              Your Teams plan includes up to 10 users (including yourself). Additional seat purchases are not available on this plan. 
                              Upgrade to Partner Pro for additional seats beyond 10.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {seats.length === 0 ? (
                      <div className="text-center py-12">
                        <UsersThree size={48} className="text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400">No additional seats purchased</p>
                        <p className="text-sm text-gray-500 mt-1">Purchase seats from Team Members page</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {seats.map((seat, index) => (
                          <div
                            key={seat.id}
                            className={`p-4 rounded-xl border ${
                              seat.cancel_at_period_end 
                                ? 'bg-red-500/5 border-red-500/20' 
                                : 'bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.1)]'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  seat.assigned_to ? 'bg-green-500/20' : 'bg-purple-500/20'
                                }`}>
                                  <UsersThree size={18} className={seat.assigned_to ? 'text-green-400' : 'text-purple-400'} />
                                </div>
                                <div>
                                  <p className="font-medium text-white">
                                    {seat.assigned_to?.name || seat.assigned_to?.email || `Seat Subscription #${index + 1}`}
                                  </p>
                                  <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400">
                                    {!seat.assigned_to && (
                                      <span className="text-amber-400">Unassigned - Available for team member</span>
                                    )}
                                    {seat.current_period_start && (
                                      <span>Started: {formatDate(seat.current_period_start)}</span>
                                    )}
                                    {seat.cancel_at_period_end && seat.current_period_end && (
                                      <span className="text-red-400 font-medium">
                                        Active until: {formatDate(seat.current_period_end)}
                                      </span>
                                    )}
                                    {!seat.cancel_at_period_end && seat.current_period_end && (
                                      <span>Renews: {formatDate(seat.current_period_end)}</span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    $15/month per seat
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                {seat.cancel_at_period_end ? (
                                  <button
                                    onClick={() => handleReinstateSeat(seat.id)}
                                    disabled={processing}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-green-400 hover:bg-green-500/10 rounded-lg transition-colors disabled:opacity-50"
                                  >
                                    {processing ? (
                                      <CircleNotch size={14} className="animate-spin" />
                                    ) : (
                                      <ArrowCounterClockwise size={14} />
                                    )}
                                    Reinstate
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setSelectedSeatToCancel(seat);
                                      setShowCancelSeatConfirm(seat.id);
                                    }}
                                    className="px-3 py-1.5 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                  >
                                    Cancel
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Info about cancellation */}
                    <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                      <div className="flex items-start gap-3">
                        <WarningCircle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-400">
                          <p className="font-medium">Seat Cancellation</p>
                          <p className="text-amber-400/80 mt-1">
                            When you cancel a seat, the assigned user can continue to use the platform until the end of their billing period.
                            You can reinstate a cancelled seat anytime before the billing period ends.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              </>
            )}
          </div>
        </motion.div>

        {/* Downgrade Confirmation Modal */}
        <AnimatePresence>
          {showDowngradeConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-[110] flex items-center justify-center bg-black/50"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-[#1A1A1A] rounded-2xl border border-[rgba(255,255,255,0.1)] p-6 max-w-lg mx-4"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                    <Warning size={24} className="text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      {showDowngradeConfirm === 'starter' ? 'Cancel Subscription?' : 'Downgrade Plan?'}
                    </h3>
                    <p className="text-sm text-gray-400">This action will take effect at end of billing period</p>
                  </div>
                </div>
                
                <p className="text-gray-300 mb-4">
                  {showDowngradeConfirm === 'starter' 
                    ? "Your subscription will be cancelled and you'll revert to the Basic (Free) plan at the end of your current billing period."
                    : showDowngradeConfirm === 'professional'
                    ? "You'll be downgraded to Professional ($39/mo) at the end of your current billing period. Team seats and multi-user access will no longer be available."
                    : "You'll be downgraded to Teams ($99/mo) at the end of your current billing period. Additional purchased seats beyond 10 will be removed."
                  }
                </p>

                {/* Impact Warning - Only show for downgrade to Basic */}
                {showDowngradeConfirm === 'starter' && (
                  <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                    <div className="flex items-start gap-3">
                      <WarningCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-semibold text-red-400 mb-2">⚠️ Important: The following will also be cancelled</p>
                        {loadingImpact ? (
                          <div className="flex items-center gap-2 text-gray-400">
                            <CircleNotch size={14} className="animate-spin" />
                            <span>Loading impact details...</span>
                          </div>
                        ) : downgradeImpact ? (
                          <ul className="space-y-2 text-gray-300">
                            {downgradeImpact.active_seats > 0 && (
                              <li className="flex items-center gap-2">
                                <CreditCard size={14} className="text-red-400" />
                                <span><strong>{downgradeImpact.active_seats}</strong> seat subscription(s) will be cancelled</span>
                              </li>
                            )}
                            {downgradeImpact.active_members > 0 && (
                              <li className="flex items-center gap-2">
                                <UsersThree size={14} className="text-red-400" />
                                <span><strong>{downgradeImpact.active_members}</strong> team member(s) will lose access</span>
                              </li>
                            )}
                            {downgradeImpact.active_agent_subscriptions > 0 && (
                              <li className="flex items-center gap-2">
                                <Robot size={14} className="text-red-400" />
                                <span><strong>{downgradeImpact.active_agent_subscriptions}</strong> agent subscription(s) will be cancelled</span>
                              </li>
                            )}
                            {downgradeImpact.total_items_affected === 0 && (
                              <li className="text-gray-400">No additional subscriptions or members will be affected.</li>
                            )}
                            {downgradeImpact.billing_period_end && (
                              <li className="text-gray-400 mt-2 pt-2 border-t border-red-500/20">
                                All cancellations take effect on: <strong>{formatDate(downgradeImpact.billing_period_end)}</strong>
                              </li>
                            )}
                          </ul>
                        ) : (
                          <p className="text-gray-400">All seat subscriptions and team members will be removed.</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowDowngradeConfirm(false);
                      setDowngradeImpact(null);
                    }}
                    className="flex-1 px-4 py-2 text-gray-400 hover:text-white transition-colors"
                  >
                    Keep Current Plan
                  </button>
                  <button
                    onClick={() => handlePlanChange(showDowngradeConfirm)}
                    disabled={processing || loadingImpact}
                    className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors disabled:opacity-50"
                  >
                    {processing ? (
                      <CircleNotch size={18} className="animate-spin mx-auto" />
                    ) : (
                      showDowngradeConfirm === 'starter' ? 'Cancel Subscription' : 'Downgrade'
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Cancel Seat Confirmation Modal */}
        <AnimatePresence>
          {showCancelSeatConfirm && selectedSeatToCancel && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-[110] flex items-center justify-center bg-black/50"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-[#1A1A1A] rounded-2xl border border-[rgba(255,255,255,0.1)] p-6 max-w-md mx-4"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <UsersThree size={24} className="text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Cancel Seat?</h3>
                    <p className="text-sm text-gray-400">
                      {selectedSeatToCancel.assigned_to?.name || selectedSeatToCancel.assigned_to?.email || 'Unassigned'}
                    </p>
                  </div>
                </div>
                
                <p className="text-gray-300 mb-6">
                  This seat will remain active until {formatDate(selectedSeatToCancel.current_period_end)}.
                  The user will continue to have access until then.
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowCancelSeatConfirm(null);
                      setSelectedSeatToCancel(null);
                    }}
                    className="flex-1 px-4 py-2 text-gray-400 hover:text-white transition-colors"
                  >
                    Keep Seat
                  </button>
                  <button
                    onClick={() => handleCancelSeat(selectedSeatToCancel.id)}
                    disabled={processing}
                    className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors disabled:opacity-50"
                  >
                    {processing ? <CircleNotch size={18} className="animate-spin mx-auto" /> : 'Cancel Seat'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </AnimatePresence>
  );
};

export default BillingManagementModal;
